import { useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type BulkActionTone = "default" | "success" | "warning" | "danger";

export interface BulkAction {
  /** Verb only — the bar supplies the count ("Approve" → "Approve 16 lines"). */
  label: string;
  onClick: () => void | Promise<void>;
  tone?: BulkActionTone;
  icon?: React.ReactNode;
  disabled?: boolean;
}

export interface BulkActionBarProps {
  /** How many rows the actions will touch — the CURRENTLY VISIBLE ones. */
  count: number;
  /** Singular noun; pluralised with a trailing "s" unless `nounPlural` is set. */
  noun?: string;
  /** Explicit plural, for nouns the trailing-"s" rule gets wrong
   *  ("company" → "companys"). */
  nounPlural?: string;
  actions: BulkAction[];
  /** Shown when some rows are filtered out, so scope is never a guess. */
  totalCount?: number;
  className?: string;
}

const TONE_CLASS: Record<BulkActionTone, string> = {
  default: "",
  success: "text-success hover:bg-success-light hover:text-success",
  warning: "text-warning hover:bg-warning-light hover:text-warning",
  danger: "text-danger hover:bg-danger-light hover:text-danger",
};

/**
 * Applies an action to every row a table is currently showing.
 *
 * Built for filtered worklists where the useful move is "everything I can see
 * right now" — filter the reconciliation to `no_data`, confirm all six at once
 * — rather than ticking a checkbox per row.
 *
 * Two things it does deliberately, because bulk actions on a filtered view are
 * easy to get wrong:
 *
 * 1. **It states the scope in the button itself.** "Approve 6 lines", never a
 *    bare "Approve all" — "all" is exactly the ambiguity that makes someone
 *    wonder whether the 181 hidden rows just moved too. When rows ARE hidden it
 *    says so alongside.
 * 2. **It confirms in place.** The first click swaps the row for
 *    "Approve 6 lines? Confirm / Cancel". No modal, but no single-click way to
 *    change 157 verdicts either.
 *
 * Use a per-row control when the rows need different answers; use this when
 * the filter has already made them the same answer.
 */
export function BulkActionBar({
  count,
  noun = "row",
  nounPlural,
  actions,
  totalCount,
  className,
}: BulkActionBarProps) {
  const [pending, setPending] = useState<BulkAction | null>(null);

  if (count === 0) return null;
  const plural = nounPlural ?? `${noun}s`;
  const label = `${count.toLocaleString()} ${count === 1 ? noun : plural}`;
  const hidden = totalCount != null && totalCount > count ? totalCount - count : 0;

  if (pending) {
    return (
      <div
        className={cn(
          "flex flex-wrap items-center gap-2 rounded-md border border-warning bg-warning-light px-3 py-2",
          className,
        )}
        role="alert"
      >
        <span className="text-sm font-medium text-warning">
          {pending.label} {label}?
        </span>
        {hidden > 0 && (
          <span className="text-xs text-warning">
            {hidden.toLocaleString()} hidden {hidden === 1 ? noun : plural} will
            NOT change.
          </span>
        )}
        <span className="ml-auto flex items-center gap-1.5">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setPending(null)}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => {
              const action = pending;
              setPending(null);
              void action.onClick();
            }}
          >
            Confirm
          </Button>
        </span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 px-3 py-2",
        className,
      )}
    >
      <span className="text-sm">
        <strong>{label}</strong> shown
        {hidden > 0 && (
          <span className="text-muted-foreground">
            {" "}
            · {hidden.toLocaleString()} hidden by filters
          </span>
        )}
      </span>
      <span className="ml-auto flex flex-wrap items-center gap-1.5">
        {actions.map((action) => (
          <Button
            key={action.label}
            size="sm"
            variant="outline"
            disabled={action.disabled}
            onClick={() => setPending(action)}
            className={cn(TONE_CLASS[action.tone ?? "default"])}
          >
            {action.icon}
            <span className={cn(action.icon && "ml-1.5")}>
              {action.label} {label}
            </span>
          </Button>
        ))}
      </span>
    </div>
  );
}
